/**
 * Review Queue — the operator's main review point. A tabbed container over the
 * distinct things an operator has to action, so review lives in one place
 * rather than scattered across pages:
 *   - Articles: articles held for review (the existing ArticlesPage review mode)
 *   - LinkedIn tags: companies whose LinkedIn org URN needs confirming
 *   - Merge candidates: entity merge pairs awaiting a decision
 *   - Recipient reviews: cold-outreach (recipient, entity) trust ledger
 *
 * Tabs are role-gated the same way the nav is (`roles.some(hasRole)`, with
 * super_admin passing all), so each operator sees only the reviews they own.
 * Add future review types as tabs here rather than as new nav silos.
 */

import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Newspaper, Linkedin, GitMerge, UserCheck } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext.js';
import { ArticlesPage } from './ArticlesPage.js';
import MergeCandidatesPage from './MergeCandidatesPage.js';
import RecipientReviewsPage from './RecipientReviewsPage.js';
import { LinkedinReviewPanel } from '../components/LinkedinReviewPanel.js';

interface ReviewTab {
  key: string;
  label: string;
  icon: ReactNode;
  roles: string[];
  render: () => ReactNode;
}

const TABS: ReviewTab[] = [
  {
    key: 'articles',
    label: 'Articles',
    icon: <Newspaper className="h-3.5 w-3.5" />,
    roles: ['social_manager', 'sales'],
    render: () => <ArticlesPage reviewQueue />,
  },
  {
    key: 'linkedin',
    label: 'LinkedIn tags',
    icon: <Linkedin className="h-3.5 w-3.5" />,
    roles: ['social_manager', 'sales', 'data_manager'],
    render: () => (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <LinkedinReviewPanel />
      </div>
    ),
  },
  {
    key: 'merge',
    label: 'Merge candidates',
    icon: <GitMerge className="h-3.5 w-3.5" />,
    roles: ['data_manager'],
    render: () => <MergeCandidatesPage />,
  },
  {
    key: 'recipient-reviews',
    label: 'Recipient reviews',
    icon: <UserCheck className="h-3.5 w-3.5" />,
    roles: ['sales'],
    render: () => <RecipientReviewsPage />,
  },
];

export function ReviewQueuePage() {
  const { hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const visible = TABS.filter((t) => t.roles.some((r) => hasRole(r)));
  const requested = searchParams.get('tab');
  const active = visible.find((t) => t.key === requested) ?? visible[0];

  const setTab = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next);
  };

  if (!active) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-sm text-grey-500 text-center">
        No reviews are assigned to your role.
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 pt-6">
        <div className="flex border-b border-grey-200 gap-1 flex-wrap">
          {visible.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-1.5 ${
                t.key === active.key
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-grey-500 hover:text-grey-700 hover:border-grey-300'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {active.render()}
    </div>
  );
}
